import { Country } from 'country-state-city';

/** Canadian province/territory ISO → IANA timezone */
const CA_PROVINCE_TIMEZONES: Record<string, string> = {
  BC: 'America/Vancouver',
  AB: 'America/Edmonton',
  SK: 'America/Regina',
  MB: 'America/Winnipeg',
  ON: 'America/Toronto',
  QC: 'America/Toronto',
  NB: 'America/Halifax',
  NS: 'America/Halifax',
  PE: 'America/Halifax',
  NL: 'America/St_Johns',
  YT: 'America/Whitehorse',
  NT: 'America/Yellowknife',
  NU: 'America/Iqaluit',
};

/** US state ISO → IANA timezone (primary zone per state) */
const US_STATE_TIMEZONES: Record<string, string> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  ID: 'America/Boise',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago',
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
  DC: 'America/New_York',
};

const COUNTRY_DEFAULT_TIMEZONES: Record<string, string> = {
  CA: 'America/Toronto',
  US: 'America/New_York',
  IN: 'Asia/Kolkata',
  GB: 'Europe/London',
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
  AE: 'Asia/Dubai',
  SG: 'Asia/Singapore',
  PK: 'Asia/Karachi',
  PH: 'Asia/Manila',
  DE: 'Europe/Berlin',
  FR: 'Europe/Paris',
  IT: 'Europe/Rome',
  ES: 'Europe/Madrid',
  JP: 'Asia/Tokyo',
  CN: 'Asia/Shanghai',
  KR: 'Asia/Seoul',
  MX: 'America/Mexico_City',
  BR: 'America/Sao_Paulo',
};

function normalizeCountryCode(country?: string): string | undefined {
  const raw = (country ?? '').trim();
  if (!raw) return undefined;
  if (raw.length === 2) return raw.toUpperCase();
  const match = Country.getAllCountries().find(
    (c) => c.isoCode.toUpperCase() === raw.toUpperCase() || c.name.toLowerCase() === raw.toLowerCase()
  );
  return match?.isoCode.toUpperCase();
}

function normalizeStateCode(state?: string): string | undefined {
  const raw = (state ?? '').trim();
  if (!raw) return undefined;
  return raw.length <= 3 ? raw.toUpperCase() : raw;
}

/**
 * Resolve IANA timezone from the client's saved country and province/state.
 * Falls back to the browser timezone when location is unknown.
 */
export function resolveClientTimeZone(country?: string, stateProvince?: string): string {
  const countryCode = normalizeCountryCode(country);
  const stateCode = normalizeStateCode(stateProvince);

  if (countryCode === 'CA' && stateCode && CA_PROVINCE_TIMEZONES[stateCode]) {
    return CA_PROVINCE_TIMEZONES[stateCode];
  }
  if (countryCode === 'US' && stateCode && US_STATE_TIMEZONES[stateCode]) {
    return US_STATE_TIMEZONES[stateCode];
  }
  if (countryCode && COUNTRY_DEFAULT_TIMEZONES[countryCode]) {
    return COUNTRY_DEFAULT_TIMEZONES[countryCode];
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}
